import type jsPDF from "jspdf";
import { isDespiaNative } from "@/lib/isDespiaNative";
import despia from "despia-native";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import i18n from "@/i18n/config";
import { getPdfViewerHandle } from "@/contexts/PdfViewerContext";

/**
 * Cross-platform PDF delivery.
 *
 * Strategy (in order of priority for the user):
 *
 * 1. Desktop browsers
 *    - `doc.save(filename)` triggers the native browser download. Already works.
 *
 * 2. Native app (Despia WebView on iOS/Android)
 *    - Open the in-app full-screen <PdfViewerOverlay /> IMMEDIATELY with a
 *      local Blob URL. This guarantees the user sees the PDF right after
 *      tapping export — no popup blockers, no native bridge dependency.
 *    - In parallel (best-effort, fire-and-forget), upload the PDF and try to
 *      hand off to the OS file viewer via the Despia `file://` bridge so the
 *      user can also share/save it through native iOS/Android UI. If that
 *      fails for any reason it does NOT affect what the user sees.
 *
 * 3. Mobile / tablet browsers (iOS Safari, Android Chrome, iPadOS)
 *    - Same in-app viewer approach (no `window.open`, no popup blockers).
 *    - Falls back to navigating the current tab to the blob URL only if the
 *      in-app viewer is not mounted (defensive — both layouts mount it).
 */

/** Touch-first / mobile-class browser detection (excludes Despia native WebView). */
function isMobileBrowser(): boolean {
  if (typeof navigator === "undefined") return false;
  if (isDespiaNative()) return false;
  const ua = navigator.userAgent || "";
  const isIpadModern =
    /Macintosh/.test(ua) && typeof navigator.maxTouchPoints === "number" && navigator.maxTouchPoints > 1;
  return isIpadModern || /iPhone|iPad|iPod|Android|Mobile|webOS|BlackBerry/i.test(ua);
}

/** Wrap a Blob as a File so the OS picks up the clean filename on Share / Save. */
function asPdfFile(blob: Blob, filename: string): Blob {
  try {
    return new File([blob], filename, { type: "application/pdf" });
  } catch {
    return blob;
  }
}

/**
 * Backwards-compatible no-op. The old implementation reserved a popup tab
 * synchronously to dodge mobile pop-up blockers. We now render PDFs inside
 * the app (PdfViewerOverlay) so no tab reservation is needed.
 */
export type PdfDeliveryTarget = {
  popup?: Window | null;
};

export function preparePdfDeliveryTarget(): PdfDeliveryTarget | undefined {
  return undefined;
}

/** Open the in-app PDF viewer with the given blob. Returns true on success. */
function openInAppPdfViewer(blob: Blob, filename: string): boolean {
  const viewer = getPdfViewerHandle();
  if (!viewer) {
    console.warn("[deliverPdf] In-app PDF viewer is not mounted yet.");
    return false;
  }

  const file = asPdfFile(blob, filename);
  const url = URL.createObjectURL(file);

  try {
    viewer.open({ url, filename, blob: file });
    return true;
  } catch (err) {
    console.warn("[deliverPdf] In-app viewer failed to open:", err);
    try { URL.revokeObjectURL(url); } catch { /* ignore */ }
    return false;
  }
}

/**
 * Native (Despia) only: upload the PDF, mint a short-lived signed URL, and open
 * it in the OS-native file viewer via the `fileviewer://` bridge — iOS renders
 * it through QuickLook (flawless pinch-zoom, share and save: the "open like
 * WhatsApp" experience), Android uses Despia's native preview.
 *
 * `fileviewer://?src=<percent-encoded https url>` is the documented Despia
 * command (NOT `file://`, which never worked). It's fire-and-forget, so a silent
 * runtime failure is possible; returns `true` only when the handoff was actually
 * dispatched (session + upload + signed URL all OK) so callers can fall back to
 * the in-app viewer when it couldn't even be attempted.
 */
export async function openPdfInNativeViewer(blob: Blob, filename: string): Promise<boolean> {
  return openFileInNativeViewer(blob, filename, "application/pdf");
}

/**
 * The same native hand-off for any file type the OS viewer understands (the
 * assistant's Excel reports: iOS QuickLook previews .xlsx and offers share /
 * save to Files). Same bucket, same 1h signed URL, same `true` = dispatched.
 */
export async function openFileInNativeViewer(blob: Blob, filename: string, contentType: string): Promise<boolean> {
  try {
    const { data: userData, error: userErr } = await supabase.auth.getUser();
    if (userErr || !userData.user) {
      console.warn("[deliverPdf] No active session for native PDF open — skipping fileviewer.");
      return false;
    }
    const userId = userData.user.id;

    const safeName = filename.replace(/[^a-zA-Z0-9._-]/g, "_");
    const path = `${userId}/${Date.now()}/${safeName}`;

    const { error: uploadErr } = await supabase.storage
      .from("pdf-exports")
      .upload(path, blob, { contentType, upsert: true });
    if (uploadErr) {
      console.warn("[deliverPdf] fileviewer upload failed:", uploadErr);
      return false;
    }

    // 1h TTL so the signed URL outlives the QuickLook session.
    const { data: signed, error: signErr } = await supabase.storage
      .from("pdf-exports")
      .createSignedUrl(path, 3600);
    if (signErr || !signed?.signedUrl) {
      console.warn("[deliverPdf] fileviewer signed URL failed:", signErr);
      return false;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- «fileviewer://» no está en los tipos de despia-native
    await despia(`fileviewer://?src=${encodeURIComponent(signed.signedUrl)}` as any);
    return true;
  } catch (err) {
    console.warn("[deliverPdf] fileviewer open failed:", err);
    return false;
  }
}

/**
 * Delivers the PDF.
 * Returns `"in-app-viewer"` when the PDF was shown inside the in-app overlay
 * (so callers can suppress redundant "exported successfully" toasts that
 * would otherwise float over the document). Returns `"download"` otherwise.
 */
export async function deliverPdf(
  doc: jsPDF,
  filename: string,
  _target?: PdfDeliveryTarget,
): Promise<"in-app-viewer" | "download"> {
  // Defensive: dismiss any stray loading toast someone else may have shown.
  try { toast.dismiss(); } catch { /* ignore */ }

  // ──────────────── 1. Native app (Despia WebView) ────────────────
  if (isDespiaNative()) {
    try {
      const blob = doc.output("blob");

      // 1a. Open in the OS-native viewer (iOS QuickLook) — flawless pinch-zoom,
      //     share and save. This is the "open like WhatsApp" path.
      if (await openPdfInNativeViewer(blob, filename)) {
        return "in-app-viewer";
      }

      // 1b. Fallback (no session / upload failed / old runtime): the in-app
      //     pdf.js viewer so the user always sees the PDF.
      if (openInAppPdfViewer(blob, filename)) {
        return "in-app-viewer";
      }

      // 1c. Last resort: navigate to a blob URL so the OS renders it inline.
      const blobUrl = URL.createObjectURL(asPdfFile(blob, filename));
      try {
        window.location.href = blobUrl;
      } catch {
        try { URL.revokeObjectURL(blobUrl); } catch { /* ignore */ }
        toast.error(i18n.t("toasts.pdf.exportError"));
      }
      return "download";
    } catch (err) {
      console.error("[deliverPdf] Native delivery failed:", err);
      toast.error(i18n.t("toasts.pdf.exportError"));
      return "download";
    }
  }

  // ──────────────── 2. Mobile / tablet browsers ────────────────
  if (isMobileBrowser()) {
    try {
      const blob = doc.output("blob");
      if (openInAppPdfViewer(blob, filename)) return "in-app-viewer";

      const blobUrl = URL.createObjectURL(asPdfFile(blob, filename));
      try {
        window.location.href = blobUrl;
      } catch {
        try { URL.revokeObjectURL(blobUrl); } catch { /* ignore */ }
        toast.error(i18n.t("toasts.pdf.exportError"));
      }
      return "download";
    } catch (err) {
      console.error("[deliverPdf] Mobile delivery failed:", err);
      try {
        doc.save(filename);
      } catch {
        toast.error(i18n.t("toasts.pdf.exportError"));
      }
      return "download";
    }
  }

  // ──────────────── 3. Desktop browser ────────────────
  doc.save(filename);
  return "download";
}

/**
 * Lo mismo que `deliverPdf`, para un PDF que ya son BYTES y no un documento de
 * jsPDF: el cuaderno de explotación sale de pdf-lib estampado sobre el modelo de
 * la Junta (lib/cuaderno). Misma cascada y mismo resultado:
 *
 *   1. app nativa → visor del sistema (QuickLook en iOS) y, si ni se puede
 *      intentar, el visor de la app o, en último caso, la URL del blob;
 *   2. móvil o tableta en el navegador → visor de la app;
 *   3. escritorio → descarga con <a download> (como useExportPdf.download).
 *
 * `deliverPdf` NO se toca a propósito: todos los PDF de jsPDF siguen por su
 * camino de siempre y este es aditivo (plan del cuaderno, CQ3).
 */
export async function deliverPdfBlob(
  pdf: Blob | Uint8Array,
  filename: string,
): Promise<"in-app-viewer" | "download"> {
  try { toast.dismiss(); } catch { /* ignore */ }

  const blob = pdf instanceof Blob ? pdf : new Blob([pdf as BlobPart], { type: "application/pdf" });
  const navigateToBlob = () => {
    const blobUrl = URL.createObjectURL(asPdfFile(blob, filename));
    try {
      window.location.href = blobUrl;
    } catch {
      try { URL.revokeObjectURL(blobUrl); } catch { /* ignore */ }
      toast.error(i18n.t("toasts.pdf.exportError"));
    }
  };
  const download = () => {
    const url = URL.createObjectURL(asPdfFile(blob, filename));
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1500);
  };

  // ──────────────── 1. Native app (Despia WebView) ────────────────
  if (isDespiaNative()) {
    try {
      if (await openPdfInNativeViewer(blob, filename)) return "in-app-viewer";
      if (openInAppPdfViewer(blob, filename)) return "in-app-viewer";
      navigateToBlob();
      return "download";
    } catch (err) {
      console.error("[deliverPdfBlob] Native delivery failed:", err);
      toast.error(i18n.t("toasts.pdf.exportError"));
      return "download";
    }
  }

  // ──────────────── 2. Mobile / tablet browsers ────────────────
  if (isMobileBrowser()) {
    try {
      if (openInAppPdfViewer(blob, filename)) return "in-app-viewer";
      navigateToBlob();
      return "download";
    } catch (err) {
      console.error("[deliverPdfBlob] Mobile delivery failed:", err);
      try {
        download();
      } catch {
        toast.error(i18n.t("toasts.pdf.exportError"));
      }
      return "download";
    }
  }

  // ──────────────── 3. Desktop browser ────────────────
  download();
  return "download";
}
